/**
 * deathGrade.ts — Performance grade for the death screen
 * =====================================================================
 * BETA 15.93 — Per Dylan: "any way they can get a specific trash talk
 * depending on how they did in game? and give them a grade? like an
 * insult name for the grade."
 *
 * Computes a letter grade S/A/B/C/D/F from a death-time stat snapshot,
 * pairs it with a peanut-flavored insult title (e.g. "F — ABSOLUTE
 * TRASH"). Used by:
 *   - DeathScreen renderer (display the badge)
 *   - deathTrashTalk selection (filter lines by grade tier)
 *
 * Tuning philosophy: most newish players land in C/D/F. S+ is rare and
 * earned (long survival OR a boss kill OR a final-boss win). The
 * comedy of the death screen depends on most grades being insulting —
 * even the "good" ones are backhanded compliments.
 * =====================================================================
 */

export type Grade = 'S' | 'A' | 'B' | 'C' | 'D' | 'F';

/**
 * Stats subset used to compute the grade. Wire from the live-play run
 * object built in livePlayTracker.ts (or from raw GameState at render
 * time — both work).
 */
export interface GradeInputs {
  survivalSeconds: number;   // state.time
  killCount: number;         // state.killCount
  levelReached: number;      // state.player.level
  damageDealt: number;       // sum from weaponSystem totalDamageDealt
  deathCause: string;        // 'enemy:zombie' / 'boss:timmy' / 'won'
}

/**
 * Map of grade letter → { title, subtitle } for the death screen badge.
 * Title is the bold all-caps line; subtitle is a smaller line below
 * adding flavor. All written in peanut voice.
 */
export const GRADE_LABELS: Record<Grade, { title: string; subtitle: string; color: string }> = {
  // S = the rare actual-good run. Even the praise is sneering.
  S: {
    title: 'PEANUT-CODED',
    subtitle: 'You are him. Briefly.',
    color: '#ffe066',     // gold
  },
  // A = solid run, peanut grudgingly nods.
  A: {
    title: 'RESPECTABLE, FOR A LOSER',
    subtitle: 'Almost made the peanut clap.',
    color: '#a8e063',     // lime
  },
  // B = mid. The most insulting grade because nobody remembers mid.
  B: {
    title: 'MID. THE WORST KIND OF MID.',
    subtitle: 'Forgettable. Like your build.',
    color: '#7eb6e6',     // pale blue
  },
  // C = walmart-tier. Functional but generic.
  C: {
    title: 'WALMART RUN',
    subtitle: 'Bargain bin gameplay. Discounted.',
    color: '#cccccc',     // gray
  },
  // D = bad. Peanut is concerned.
  D: {
    title: 'BRO WHAT WAS THAT',
    subtitle: 'The peanut is calling your mom.',
    color: '#e69b3c',     // orange
  },
  // F = absolute trash. The classic.
  F: {
    title: 'ABSOLUTE TRASH',
    subtitle: 'Delete the save file. Start over. Be better.',
    color: '#ff3d3d',     // red
  },
};

/**
 * Score-out-of-100 rubric:
 *
 *   Survival (max 50)   → 5 points per minute survived, capped at 10 min
 *   Kills (max 30)      → 0.15 per kill, capped at 200 kills
 *   Level (max 20)      → 1 per level, capped at level 20
 *   Death-cause bonuses → +50 for 'won', +20 for boss death (whether you killed it or it killed you)
 *
 * Total clamped to 100. Then mapped:
 *   S: 90-100  (rare)
 *   A: 75-89
 *   B: 60-74
 *   C: 45-59
 *   D: 30-44
 *   F: 0-29   (most early deaths)
 *
 * Tunable — drop the thresholds if too few players hit S/A. Bump them
 * if every run gets an A.
 */
export function computeGrade(stats: GradeInputs): { grade: Grade; score: number } {
  let score = 0;
  // Survival contribution
  const survivalMins = stats.survivalSeconds / 60;
  score += Math.min(10, survivalMins) * 5;
  // Kill contribution
  score += Math.min(200, stats.killCount) * 0.15;
  // Level contribution
  score += Math.min(20, stats.levelReached);
  // Death-cause bonus
  if (stats.deathCause === 'won') {
    score += 50; // huge bonus for beating the final boss
  } else if (stats.deathCause.startsWith('boss:')) {
    score += 20; // moderate bonus for engaging a boss (even if you died to it)
  }
  // Clamp
  score = Math.max(0, Math.min(100, Math.round(score)));

  let grade: Grade = 'F';
  if (score >= 90) grade = 'S';
  else if (score >= 75) grade = 'A';
  else if (score >= 60) grade = 'B';
  else if (score >= 45) grade = 'C';
  else if (score >= 30) grade = 'D';
  // else stays F

  return { grade, score };
}
